/**
 * @file: OriginIntentionBar.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * The 线索来源 + 到店意向 control row. Each pill drives one filter field
 * (`filter.origin` / `filter.intention`); picking `全部` drops that field so the
 * list widens again. Both option sets are short, so they open as action sheets
 * rather than a searchable page.
 */

import { LD } from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import {
  LeadValueOption,
  kLeadIntentionOptions,
  kLeadOriginOptions,
} from '@euler/app/flows/leads/pages/list/functions/leadListConstants';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { Gauge, Tag } from 'lucide-react-native';
import { memo, useCallback } from 'react';
import { View } from 'react-native';
import { ControlPill } from './ControlPill';

const labelOf = <T,>(options: LeadValueOption<T>[], key: string): string =>
  (options.find(o => o.key === key) ?? options[0]).label;

export const OriginIntentionBar = memo(
  ({
    originKey,
    onSelectOrigin,
    intentionKey,
    onSelectIntention,
  }: {
    originKey: string;
    onSelectOrigin: (key: string) => void;
    intentionKey: string;
    onSelectIntention: (key: string) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { showActionSheetWithOptions } = useActionSheet();

    const openSheet = useCallback(
      <T,>(
        options: LeadValueOption<T>[],
        title: string,
        onSelect: (key: string) => void,
      ) => {
        showActionSheetWithOptions(
          {
            options: [...options.map(o => o.label), '取消'],
            cancelButtonIndex: options.length,
            title,
          },
          selected => {
            if (selected == null || selected >= options.length) return;
            onSelect(options[selected].key);
          },
        );
      },
      [showActionSheetWithOptions],
    );

    return (
      <View
        style={{
          flexDirection: 'row',
          gap: rpx(10),
          marginHorizontal: rpx(16),
          marginBottom: rpx(10),
        }}
      >
        <ControlPill
          icon={<Tag size={rpx(16)} color={LD.steel} />}
          label={`来源：${labelOf(kLeadOriginOptions, originKey)}`}
          onPress={() =>
            openSheet(kLeadOriginOptions, '线索来源', onSelectOrigin)
          }
        />
        <ControlPill
          icon={<Gauge size={rpx(16)} color={LD.steel} />}
          label={`意向：${labelOf(kLeadIntentionOptions, intentionKey)}`}
          onPress={() =>
            openSheet(kLeadIntentionOptions, '到店意向', onSelectIntention)
          }
        />
      </View>
    );
  },
);
